import { useCallback, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { LIST_WIDTH, updatePreferences, usePreferences } from "@/lib/preferences";

const DEFAULT_WIDTH = LIST_WIDTH.default;
const MIN_WIDTH = LIST_WIDTH.min;
const MAX_WIDTH = LIST_WIDTH.max;
/** Always leave the reading pane at least this much room. */
const READER_MIN = 360;

/**
 * The message-list column, resizable Outlook-style when the reading pane sits
 * on its right: drag the handle, use ←/→ on it, or double-click it to reset.
 * The width is a saved preference (this browser + the account). With the
 * reader at the bottom or hidden, the column is laid out by `className` alone
 * and no handle renders.
 */
export function ResizableListColumn({
    enabled,
    className,
    children
}: {
    enabled: boolean;
    className?: string;
    children: ReactNode;
}) {
    const columnRef = useRef<HTMLDivElement>(null);
    const saved = usePreferences((p) => p.layout.listWidth);
    // While dragging, the width tracks the pointer locally and is saved once on release.
    const [dragWidth, setDragWidth] = useState<number | null>(null);
    const width = dragWidth ?? saved;

    const clamp = useCallback((w: number) => {
        const container = columnRef.current?.parentElement?.clientWidth ?? Infinity;
        const max = Math.min(MAX_WIDTH, container - READER_MIN);
        return Math.round(Math.max(MIN_WIDTH, Math.min(w, Math.max(MIN_WIDTH, max))));
    }, []);

    const commit = useCallback((w: number) => {
        setDragWidth(null);
        updatePreferences({ layout: { listWidth: Math.round(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w))) } });
    }, []);

    const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = columnRef.current?.getBoundingClientRect().width ?? width;
        const handle = e.currentTarget;
        handle.setPointerCapture(e.pointerId);
        setDragWidth(startWidth);
        let latest = startWidth;
        const onMove = (ev: PointerEvent) => {
            latest = clamp(startWidth + ev.clientX - startX);
            setDragWidth(latest);
        };
        const onUp = () => {
            handle.removeEventListener("pointermove", onMove);
            handle.removeEventListener("pointerup", onUp);
            handle.removeEventListener("pointercancel", onUp);
            commit(latest);
        };
        handle.addEventListener("pointermove", onMove);
        handle.addEventListener("pointerup", onUp);
        handle.addEventListener("pointercancel", onUp);
    };

    const dragging = dragWidth !== null;

    return (
        <>
            <div
                ref={columnRef}
                className={cn(className, enabled && "flex-none")}
                style={enabled ? { width } : undefined}
            >
                {children}
            </div>
            {enabled ? (
                <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize message list"
                    aria-valuenow={width}
                    aria-valuemin={MIN_WIDTH}
                    aria-valuemax={MAX_WIDTH}
                    tabIndex={0}
                    title="Drag to resize · double-click to reset"
                    onPointerDown={onPointerDown}
                    onDoubleClick={() => commit(clamp(DEFAULT_WIDTH))}
                    onKeyDown={(e) => {
                        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                            e.preventDefault();
                            commit(clamp(width + (e.key === "ArrowRight" ? 24 : -24)));
                        }
                    }}
                    className={cn(
                        // Doubles as the 8 px gutter between the list and reader cards.
                        "group/resize relative z-10 flex w-2 shrink-0 cursor-col-resize touch-none items-center justify-center",
                        "focus:outline-none"
                    )}
                >
                    <span
                        aria-hidden
                        className={cn(
                            "h-10 w-1 rounded-full bg-border transition-colors",
                            "group-hover/resize:bg-primary/60 group-focus-visible/resize:bg-primary",
                            dragging && "bg-primary"
                        )}
                    />
                </div>
            ) : null}
        </>
    );
}
